import React from 'react';
import styled from 'styled-components';
import { BarChart3, Trophy, Medal, Award } from 'lucide-react';
import { MonthlyPerformance, formatPercentage } from '../../../services/dashboardService';

const Card = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  padding: 28px;
  margin-bottom: 28px;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const CardTitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CardIcon = styled.div`
  display: flex;
  align-items: center;
  color: var(--primary-color);
  filter: saturate(0.7) brightness(0.9);
`;

const CardTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
`;

const MonthBadge = styled.span`
  background: var(--primary-light);
  color: var(--primary-color);
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  filter: saturate(0.7) brightness(0.9);
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const MetricCard = styled.div<{ $accentColor: string }>`
  background: white;
  border-radius: 12px;
  padding: 24px;
  padding-top: 28px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--border-color);
  position: relative;
  overflow: hidden;
  transition: all 0.2s ease;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.$accentColor};
  }

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
`;

const MetricValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 4px;
`;

const MetricLabel = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
  font-weight: 500;
  margin-bottom: 8px;
`;

const MetricChange = styled.div<{ $positive?: boolean }>`
  font-size: 12px;
  color: ${props => props.$positive ? '#059669' : '#94a3b8'};
  display: flex;
  align-items: center;
  gap: 4px;
`;

const RankingSection = styled.div`
  margin-top: 24px;
  padding: 20px;
  background: var(--background-color);
  border-radius: 10px;
  text-align: center;
`;

const RankingTitle = styled.h4`
  font-size: 14px;
  font-weight: 500;
  color: var(--text-secondary);
  margin-bottom: 12px;
`;

const RankingDisplay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const RankNumber = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: var(--primary-color);
  filter: saturate(0.7) brightness(0.9);
`;

const RankTotal = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
`;

const TrophyIcon = styled.div<{ $rank: number }>`
  display: flex;
  align-items: center;
  color: ${props => {
    if (props.$rank === 1) return '#d97706';
    if (props.$rank === 2) return '#6b7280';
    if (props.$rank === 3) return '#92400e';
    return 'transparent';
  }};
  filter: saturate(0.7) brightness(0.9);
`;

interface SalesPerformanceCardProps {
  performance: MonthlyPerformance;
  teamPerformance?: MonthlyPerformance;
  isSupervisor?: boolean;
}

const TabContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  padding-bottom: 16px;
`;

const Tab = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: ${props => props.$active ? 'var(--primary-color)' : 'white'};
  border: 1px solid ${props => props.$active ? 'var(--primary-color)' : 'var(--border-color)'};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.$active ? 'white' : 'var(--text-secondary)'};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--primary-color);
    color: ${props => props.$active ? 'white' : 'var(--primary-color)'};
  }
`;

const SalesPerformanceCard: React.FC<SalesPerformanceCardProps> = ({ performance, teamPerformance, isSupervisor }) => {
  const [activeTab, setActiveTab] = React.useState<'personal' | 'team'>('personal');
  const currentMonth = new Date().toLocaleDateString('he-IL', { month: 'long', year: 'numeric' });

  const getRankingIcon = (rank: number) => {
    if (rank === 1) return <Trophy size={24} />;
    if (rank === 2) return <Medal size={24} />;
    if (rank === 3) return <Award size={24} />;
    return null;
  };

  const displayPerformance = activeTab === 'personal' ? performance : (teamPerformance || performance);

  return (
    <Card>
      <CardHeader>
        <CardTitleContainer>
          <CardIcon>
            <BarChart3 size={20} />
          </CardIcon>
          <CardTitle>ביצועים חודשיים</CardTitle>
        </CardTitleContainer>
        <MonthBadge>{currentMonth}</MonthBadge>
      </CardHeader>

      {isSupervisor && teamPerformance && (
        <TabContainer>
          <Tab
            $active={activeTab === 'personal'}
            onClick={() => setActiveTab('personal')}
          >
            אישי
          </Tab>
          <Tab
            $active={activeTab === 'team'}
            onClick={() => setActiveTab('team')}
          >
            צוות
          </Tab>
        </TabContainer>
      )}

      <MetricsGrid>
        <MetricCard $accentColor="#6366f1">
          <MetricValue>{displayPerformance.newLeadsReceived}</MetricValue>
          <MetricLabel>לידים חדשים {activeTab === 'team' && '(צוות)'}</MetricLabel>
          <MetricChange $positive>
            התקבלו החודש
          </MetricChange>
        </MetricCard>

        <MetricCard $accentColor="#0d9488">
          <MetricValue>{displayPerformance.signatures}</MetricValue>
          <MetricLabel>חתימות {activeTab === 'team' && '(צוות)'}</MetricLabel>
          <MetricChange $positive={displayPerformance.signatures > 0}>
            {displayPerformance.signatures > 0 ? 'הושלמו' : 'ממתין'}
          </MetricChange>
        </MetricCard>

        <MetricCard $accentColor="#f59e0b">
          <MetricValue>{formatPercentage(displayPerformance.conversionRate)}</MetricValue>
          <MetricLabel>אחוז המרה {activeTab === 'team' && '(צוות)'}</MetricLabel>
          <MetricChange $positive={displayPerformance.conversionRate > 20}>
            {displayPerformance.conversionRate > 20 ? 'מעל היעד' : 'מתחת ליעד'}
          </MetricChange>
        </MetricCard>
      </MetricsGrid>

      {activeTab === 'personal' && (
        <RankingSection>
          <RankingTitle>דירוג בצוות</RankingTitle>
          <RankingDisplay>
            {performance.teamRanking <= 3 && (
              <TrophyIcon $rank={performance.teamRanking}>
                {getRankingIcon(performance.teamRanking)}
              </TrophyIcon>
            )}
            <RankNumber>#{performance.teamRanking}</RankNumber>
            <RankTotal>מתוך {performance.totalTeamMembers} נציגים</RankTotal>
          </RankingDisplay>
        </RankingSection>
      )}
    </Card>
  );
};

export default SalesPerformanceCard;
